import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { storage, db } from '../firebase';
import pako from 'pako';

/**
 * Compress file data using gzip
 */
const compressFile = (content) => {
  const uint8Array = new TextEncoder().encode(content);
  return pako.gzip(uint8Array, { level: 6 }); // Level 6 = faster compression
};

/**
 * Decompress gzipped file data
 */
const decompressFile = (compressed) => {
  const decompressed = pako.ungzip(compressed);
  return new TextDecoder().decode(decompressed);
};

/**
 * Save analysis with compressed file - STORAGE ONLY
 * Stores ONLY summary stats in Firestore, full data in Storage
 * @param {string} userId - The user's Firebase UID
 * @param {File} rawFile - The original file
 * @param {string} fileContent - File content as string
 * @param {Object} analysisResults - The analysis results with SNPs
 * @returns {Promise<Object>} The saved document
 */
export const saveAnalysisWithFile = async (userId, rawFile, fileContent, analysisResults) => {
  console.log('💾 Saving analysis...');
  console.log('   File:', rawFile.name, '(' + formatFileSize(rawFile.size) + ')');
  
  try {
    const timestamp = Date.now();
    const sanitizedFileName = rawFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    
    // 1. Compress the raw file
    console.log('   🗜️ Compressing file...');
    const startCompress = Date.now();
    const compressed = compressFile(fileContent);
    const compressTime = ((Date.now() - startCompress) / 1000).toFixed(1);
    const compressionRatio = ((1 - compressed.length / rawFile.size) * 100).toFixed(1);
    
    console.log(`   ✅ Compressed in ${compressTime}s:`, formatFileSize(compressed.length), `(${compressionRatio}% smaller)`);
    
    // 2. Upload compressed raw file
    console.log('   ⬆️ Uploading compressed file...');
    const startUpload = Date.now();
    const rawFilePath = `users/${userId}/genetic-files/${timestamp}_${sanitizedFileName}.gz`;
    const rawFileRef = ref(storage, rawFilePath);
    
    await uploadBytes(rawFileRef, compressed, {
      contentType: 'application/gzip',
      customMetadata: {
        originalName: rawFile.name,
        originalSize: rawFile.size.toString()
      }
    });
    
    const uploadTime = ((Date.now() - startUpload) / 1000).toFixed(1);
    console.log(`   ✅ Upload complete in ${uploadTime}s`);
    
    const rawFileURL = await getDownloadURL(rawFileRef);
    
    // 3. Create a separate JSON file with analysis results
    console.log('   📊 Creating analysis data file...');
    const analysisData = {
      snps: analysisResults.snps.map(snp => ({
        rsid: snp.rsid || null,
        chromosome: snp.chromosome || null,
        position: snp.position || null,
        genotype: snp.genotype || null,
        gene: snp.gene || null,
        significance: snp.significance || null,
        conditions: snp.conditions || null,
        riskCategory: snp.riskCategory || null,
        gwas: snp.gwas || null
      }))
    };
    
    const analysisJSON = JSON.stringify(analysisData);
    const analysisBlob = new Blob([analysisJSON], { type: 'application/json' });
    
    const analysisPath = `users/${userId}/genetic-files/${timestamp}_${sanitizedFileName}_analysis.json`;
    const analysisRef = ref(storage, analysisPath);
    
    await uploadBytes(analysisRef, analysisBlob);
    const analysisURL = await getDownloadURL(analysisRef);
    
    console.log('   ✅ Analysis data uploaded');
    
    // 4. Save metadata to Firestore (SMALL document, no SNP data)
    const firestoreDoc = {
      userId,
      fileName: rawFile.name,
      uploadedAt: new Date(),
      lastAnalyzedAt: new Date(),
      
      // File info
      originalSize: rawFile.size,
      compressedSize: compressed.length,
      compressionRatio: parseFloat(compressionRatio),
      
      // Storage paths
      rawFilePath,
      rawFileURL,
      analysisPath,
      analysisURL,
      isCompressed: true,
      
      // Summary statistics ONLY (not full SNP data)
      stats: {
        totalSnps: analysisResults.totalSnps || 0,
        analyzedSnps: analysisResults.analyzedSnps || 0,
        pathogenic: analysisResults.snps?.filter(s => s.significance?.includes('Pathogenic')).length || 0,
        benign: analysisResults.snps?.filter(s => s.significance?.includes('Benign')).length || 0,
        likelyPathogenic: analysisResults.snps?.filter(s => s.significance?.includes('Likely pathogenic')).length || 0,
        uncertainSignificance: analysisResults.snps?.filter(s => s.significance?.includes('Uncertain')).length || 0,
        
        // Top findings preview (first 10 pathogenic for quick view)
        topFindings: analysisResults.snps
          .filter(s => s.significance?.includes('Pathogenic'))
          .slice(0, 10)
          .map(s => ({
            rsid: s.rsid,
            gene: s.gene,
            significance: s.significance
          }))
      }
    };
    
    console.log('   💾 Saving metadata to Firestore...');
    const docRef = await addDoc(collection(db, 'geneticAnalyses'), firestoreDoc);
    
    console.log('   ✅ Analysis saved with ID:', docRef.id);
    console.log(`✅ SAVE COMPLETE! Total time: ${compressTime}s + ${uploadTime}s`);
    
    return {
      id: docRef.id,
      ...firestoreDoc
    };
  } catch (error) {
    console.error('❌ ERROR saving analysis:', error);
    throw new Error(`Failed to save analysis: ${error.message}`);
  }
};

/**
 * Get all analyses for a user
 */
export const getUserAnalyses = async (userId) => {
  console.log('📂 Getting analyses for user:', userId);
  
  try {
    const q = query(
      collection(db, 'geneticAnalyses'),
      where('userId', '==', userId),
      orderBy('uploadedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const analyses = [];
    
    querySnapshot.forEach((doc) => {
      analyses.push({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
        lastAnalyzedAt: doc.data().lastAnalyzedAt?.toDate()
      });
    });
    
    console.log(`   ✅ Retrieved ${analyses.length} analyses`);
    return analyses;
  } catch (error) {
    console.error('❌ ERROR in getUserAnalyses:', error);
    throw new Error(`Failed to retrieve analyses: ${error.message}`);
  }
};

/**
 * Load analysis from Storage (downloads JSON file)
 */
export const loadAnalysis = async (analysisId) => {
  console.log('📖 Loading analysis:', analysisId);
  
  try {
    const { getDoc } = await import('firebase/firestore');
    const analysisRef = doc(db, 'geneticAnalyses', analysisId);
    const analysisDoc = await getDoc(analysisRef);
    
    if (!analysisDoc.exists()) {
      throw new Error('Analysis not found');
    }
    
    const data = analysisDoc.data();
    
    if (!data.analysisURL) {
      throw new Error('Analysis data URL not found');
    }
    
    // Download analysis JSON from Storage
    console.log('   📥 Downloading analysis data from:', data.analysisURL);
    
    const response = await fetch(data.analysisURL, {
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch analysis data: ${response.statusText}`);
    }
    
    const analysisData = await response.json();
    
    console.log('   ✅ Analysis data loaded:', analysisData.snps?.length, 'SNPs');
    
    // Return in expected format
    return {
      totalSnps: data.stats.totalSnps,
      analyzedSnps: data.stats.analyzedSnps,
      snps: analysisData.snps || [],
      isPartial: false
    };
  } catch (error) {
    console.error('❌ Error loading analysis:', error);
    console.error('   Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

/**
 * Download compressed raw file and decompress
 */
export const downloadRawFile = async (downloadURL, fileName) => {
  console.log('📥 Downloading raw file...');
  
  try {
    // Use download URL directly - no fetch needed for downloads
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadURL;
    link.download = fileName.replace(/\.gz$/, ''); // Remove .gz if present
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // For compressed files, we need to fetch, decompress, then download
    if (downloadURL.includes('.gz')) {
      const response = await fetch(downloadURL, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
      const compressedData = await response.arrayBuffer();
      
      console.log('   🗜️ Decompressing...');
      const decompressed = decompressFile(new Uint8Array(compressedData));
      
      // Create download link with decompressed data
      const blob = new Blob([decompressed], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      link.href = url;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Direct download for non-compressed files
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    console.log('✅ File downloaded');
  } catch (error) {
    console.error('❌ Error downloading file:', error);
    
    // Fallback: open in new tab if CORS fails
    if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
      console.log('   ⚠️ CORS error, opening file in new tab...');
      window.open(downloadURL, '_blank');
    } else {
      throw error;
    }
  }
};

/**
 * Delete an analysis and all associated files
 */
export const deleteAnalysis = async (analysisId, rawFilePath = null, analysisPath = null) => {
  console.log('🗑️ Deleting analysis:', analysisId);
  
  try {
    // Delete raw file from Storage
    if (rawFilePath) {
      try {
        await deleteObject(ref(storage, rawFilePath));
        console.log('   ✅ Raw file deleted');
      } catch (error) {
        console.warn('   ⚠️ Could not delete raw file:', error.message);
      }
    }
    
    // Delete analysis JSON from Storage
    if (analysisPath) {
      try {
        await deleteObject(ref(storage, analysisPath));
        console.log('   ✅ Analysis file deleted');
      } catch (error) {
        console.warn('   ⚠️ Could not delete analysis file:', error.message);
      }
    }
    
    // Delete from Firestore
    await deleteDoc(doc(db, 'geneticAnalyses', analysisId));
    console.log('   ✅ Metadata deleted');
    
    console.log('✅ Delete complete');
  } catch (error) {
    console.error('❌ Error deleting analysis:', error);
    throw error;
  }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};